import { api } from './api';

export interface GalleryImage {
  url: string;
  alt: string;
}

export interface GalleryItem {
  id: number;
  title: string;
  description: string;
  category?: string;
  location?: string;
  beforeImage: string;
  afterImage: string;
  beforeThumbnail?: string;
  afterThumbnail?: string;
  images: GalleryImage[]; // Required for backward compatibility
  createdAt: string;
  updatedAt?: string;
}

class GalleryService {
  private cache: GalleryItem[] | null = null;
  private cacheExpiry: number = 0;
  private cacheDuration = 5 * 60 * 1000; // 5 minutes

  async getGalleryItems(): Promise<GalleryItem[]> {
    const now = Date.now();
    
    // Return cached data if still valid
    if (this.cache && now < this.cacheExpiry) {
      return this.cache as GalleryItem[];
    }

    try {
      const response = await api.getGallery();
      const items = response.data;
      
      // Transform items to ensure backward compatibility
      this.cache = items.map((item: any) => {
        let galleryImages: GalleryImage[] = [];
        
        // Jeśli ma nowy format z images jako string[]
        if (item.images && Array.isArray(item.images)) {
          galleryImages = item.images.map((url: string, index: number) => ({
            url: url,
            alt: `${item.title} - zdjęcie ${index + 1}`
          }));
        }
        // Jeśli ma stary format z beforeImage/afterImage
        else if (item.beforeImage && item.afterImage) {
          galleryImages = [
            { url: item.beforeImage, alt: `${item.title} - przed` },
            { url: item.afterImage, alt: `${item.title} - po` }
          ];
        }
        
        return {
          ...item,
          images: galleryImages
        };
      });
      
      this.cacheExpiry = now + this.cacheDuration;
      return this.cache as GalleryItem[];
    } catch (error) {
      console.error('Error fetching gallery items:', error);
      
      // Return fallback data if API fails
      return this.getFallbackData();
    }
  }

  async getAllGalleryItems(): Promise<GalleryItem[]> {
    return this.getGalleryItems();
  }

  async getGalleryItemById(id: number): Promise<GalleryItem | null> {
    const items = await this.getGalleryItems();
    return items.find(item => item.id === id) || null;
  }

  invalidateCache(): void {
    this.cache = null;
    this.cacheExpiry = 0;
  }

  private getFallbackData(): GalleryItem[] {
    return [
      {
        id: 1,
        title: "Nowoczesny ogród zimowy w Poznaniu",
        description: "Realizacja ogrodu zimowego w nowoczesnym stylu z pełnym przeszkleniem i systemem wentylacji.",
        category: "Ogród zimowy",
        location: "Poznań",
        beforeImage: "/src/assets/projekt-1.png",
        afterImage: "/src/assets/projekt-1.png",
        images: [
          { url: "/src/assets/projekt-1.png", alt: "Nowoczesny ogród zimowy - przed" },
          { url: "/src/assets/projekt-1.png", alt: "Nowoczesny ogród zimowy - po" }
        ],
        createdAt: new Date('2023-05-15').toISOString()
      }
    ];
  }

  // For backward compatibility - keep the old ImgBB method
  async uploadImageToImgbb(file: File): Promise<string> {
    // This is now handled by the backend API
    const formData = new FormData();
    formData.append('images', file);
    
    try {
      const response = await api.uploadImages(formData);
      const uploadedFiles = response.data.files;
      return uploadedFiles[0]?.original || '';
    } catch (error) {
      console.error('Upload error:', error);
      throw new Error('Failed to upload image');
    }
  }

  async addGalleryItem(item: any): Promise<GalleryItem> {
    try {
      // Convert old format to new format
      const newItem = {
        title: item.title,
        description: item.description,
        category: item.category || '',
        location: item.location || '',
        beforeImage: item.beforeImage || (item.images && item.images[0]?.url) || '',
        afterImage: item.afterImage || (item.images && item.images[1]?.url) || '',
      };

      const response = await api.addGalleryItem(newItem);
      this.invalidateCache();
      return response.data;
    } catch (error) {
      console.error('Error adding gallery item:', error);
      throw error;
    }
  }

  async updateGalleryItem(id: number, item: Partial<GalleryItem>): Promise<GalleryItem> {
    try {
      const response = await api.updateGalleryItem(id, item);
      this.invalidateCache();
      return response.data;
    } catch (error) {
      console.error('Error updating gallery item:', error);
      throw error;
    }
  }

  async deleteGalleryItem(id: number): Promise<void> {
    try {
      await api.deleteGalleryItem(id);
      this.invalidateCache();
    } catch (error) {
      console.error('Error deleting gallery item:', error);
      throw error;
    }
  }

  // Mock admin methods for backward compatibility
  isAdminLoggedIn(): boolean {
    return false; // Will be handled by GalleryAdmin component
  }

  async adminLogin(_login: string, _password: string): Promise<boolean> {
    // This will be handled by GalleryAdmin component
    return false;
  }

  adminLogout(): void {
    // This will be handled by GalleryAdmin component
  }
}

export const galleryService = new GalleryService();
